/**
 * Edit Plan Permissions Modal
 *
 * Checkbox grid for managing the fine-grained permission allow-list on a plan.
 * Permissions are grouped by `module`; permissions with module === null are shown
 * in an "Always Available" section at the bottom with a note explaining they are
 * never license-gated (they pass through RBAC regardless of plan), but may still
 * be explicitly included here for auditability.
 *
 * Reachable via the "Permissions" action on each plan row in PlanManagementPage.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Shield } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import planService, { Plan } from '../../services/api/planService';
import permissionService, { Permission } from '../../services/api/permissionService';
import { extractApiError } from '../../utils/apiError';
import { MODULE_LABELS } from './EditPlanModulesModal';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

/**
 * Sentinel map key for permissions whose `module` is null.
 * These are never license-gated; they pass through RBAC regardless of plan.
 */
const NULL_MODULE_KEY = '__null_module__';
const NULL_MODULE_LABEL = 'Always Available (not license-gated)';

/** Stable query key for the full permission catalog — never changes. */
const CATALOG_QUERY_KEY = ['permissions-catalog'] as const;

/**
 * Flatten the `{ [resource]: Permission[] }` catalog and sort into module groups.
 * Returns an ordered map: module key → permission list.
 * The null-module group is appended last.
 */
function groupByModule(grouped: Record<string, Permission[]>): Map<string, Permission[]> {
  const flat = Object.values(grouped).flat();

  // Sort: by module (null last), then by displayName within each module
  flat.sort((a, b) => {
    if (a.module === b.module) return a.displayName.localeCompare(b.displayName);
    if (a.module === null) return 1;
    if (b.module === null) return -1;
    return a.module!.localeCompare(b.module!);
  });

  const map = new Map<string, Permission[]>();
  for (const perm of flat) {
    const key = perm.module ?? NULL_MODULE_KEY;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(perm);
  }
  return map;
}

/**
 * Human-readable label for a module key.
 * Reuses MODULE_LABELS from EditPlanModulesModal for consistent display
 * (e.g. "links" → "Shareable Links" not just "Links").
 */
function moduleLabel(key: string): string {
  if (key === NULL_MODULE_KEY) return NULL_MODULE_LABEL;
  return (MODULE_LABELS as Record<string, string>)[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EditPlanPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: Plan;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EditPlanPermissionsModal = ({ isOpen, onClose, plan }: EditPlanPermissionsModalProps) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /**
   * Tracks which plan's enabled set has been used to initialise `selected` in
   * the current open session.  Set to `plan.id` after the first hydration, and
   * cleared back to `null` when the modal closes.  This prevents two failure
   * modes:
   *   1. Stale selection leaking from Plan A to Plan B (fixed by eagerly
   *      clearing `selected` the moment the plan changes / modal opens, before
   *      the async fetch for the new plan completes).
   *   2. Background refetches overwriting in-progress user edits within the
   *      same open session (the Effect 2 guard `initializedPlanRef.current ===
   *      plan.id` short-circuits once hydrated).
   */
  const initializedPlanRef = useRef<string | null>(null);

  const enabledQueryKey = useMemo(() => ['plan-permissions', plan.id], [plan.id]);
  const modulesQueryKey = useMemo(() => ['plan-modules', plan.id], [plan.id]);

  // Full permission catalog — stale for 5 min since it changes rarely
  const {
    data: catalogGrouped,
    isLoading: isLoadingCatalog,
    isError: isErrorCatalog,
  } = useQuery({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: () => permissionService.getAllPermissions(),
    staleTime: 5 * 60 * 1000,
  });

  // Currently-enabled permission IDs for this plan
  const {
    data: enabledPerms,
    isLoading: isLoadingEnabled,
    isError: isErrorEnabled,
  } = useQuery({
    queryKey: enabledQueryKey,
    queryFn: () => planService.getPlanPermissions(plan.id),
    enabled: isOpen,
    staleTime: 30_000,
  });

  // Enabled module keys for this plan — gates which module groups are shown
  const {
    data: enabledModules,
    isLoading: isLoadingModules,
    isError: isErrorModules,
  } = useQuery({
    queryKey: modulesQueryKey,
    queryFn: () => planService.getPlanModules(plan.id),
    enabled: isOpen,
    staleTime: 30_000,
  });

  /** True when any bootstrap query failed — saving in this state would destructively overwrite the allowlist. */
  const isBootstrapError = isErrorCatalog || isErrorEnabled || isErrorModules;

  /** Set of enabled module keys; null-module permissions are never gated. */
  const enabledModulesSet = useMemo(
    () => (enabledModules ? new Set(enabledModules) : null),
    [enabledModules],
  );

  const permissionsByModule = useMemo(
    () => (catalogGrouped ? groupByModule(catalogGrouped) : new Map<string, Permission[]>()),
    [catalogGrouped],
  );

  /**
   * Filtered view: only render groups whose module key is in the plan's enabled
   * modules set.  The NULL_MODULE_KEY group is always kept — those permissions
   * are never license-gated.  When enabledModulesSet is not yet loaded we fall
   * back to the full map so we don't flash an empty state during loading.
   */
  const visiblePermissionsByModule = useMemo(() => {
    if (!enabledModulesSet) return permissionsByModule;
    const filtered = new Map<string, Permission[]>();
    for (const [key, perms] of permissionsByModule.entries()) {
      if (key === NULL_MODULE_KEY || enabledModulesSet.has(key)) {
        filtered.set(key, perms);
      }
    }
    return filtered;
  }, [permissionsByModule, enabledModulesSet]);

  /**
   * Effect 1 — eager reset on open / plan change.
   *
   * Fires as soon as `isOpen` or `plan.id` changes.  When the modal is closed,
   * the init flag is cleared so the next open triggers a fresh hydration.
   * When the modal opens (or the target plan changes while already open),
   * `selected` is immediately wiped so Plan A's IDs can never linger while
   * Plan B's async fetch is in-flight.
   */
  useEffect(() => {
    if (!isOpen) {
      initializedPlanRef.current = null;
      setSuccessMessage(null);
      return;
    }
    // Modal is open — if we haven't initialised for this plan yet, clear selection
    // so the UI never shows stale data from a previously-viewed plan.
    if (initializedPlanRef.current !== plan.id) {
      setSelected(new Set());
      setSuccessMessage(null);
    }
  }, [isOpen, plan.id]);

  /**
   * Effect 2 — hydrate from fetched data (once per open session).
   *
   * Runs whenever the enabled-perms or enabled-modules query results arrive.
   * Waits for both before hydrating so we can immediately filter out permissions
   * whose module is not in the plan's enabled set.  The `initializedPlanRef`
   * guard means we hydrate exactly once per plan-open session; subsequent
   * background refetches do NOT overwrite user edits.
   *
   * Null-module permissions are never gated and are always included.
   */
  useEffect(() => {
    if (!isOpen || !enabledPerms || !enabledModulesSet) return;
    if (initializedPlanRef.current === plan.id) return; // already hydrated — protect user edits
    const validIds = enabledPerms
      .filter((p) => p.module === null || enabledModulesSet.has(p.module))
      .map((p) => p.permissionId);
    setSelected(new Set(validIds));
    initializedPlanRef.current = plan.id;
  }, [isOpen, plan.id, enabledPerms, enabledModulesSet]);

  // Auto-clear success message whenever the user makes a new selection change.
  useEffect(() => {
    setSuccessMessage(null);
  }, [selected]);

  const updateMutation = useMutation({
    mutationFn: (ids: string[]) => planService.setPlanPermissions(plan.id, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enabledQueryKey });
      setSuccessMessage('Permissions updated successfully.');
    },
  });

  const handleClose = () => {
    updateMutation.reset();
    onClose();
  };

  const handleToggle = (permId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  };

  /** Toggle all permissions in a module group on/off. */
  const handleModuleToggle = (moduleKey: string) => {
    const permsInModule = visiblePermissionsByModule.get(moduleKey) ?? [];
    const allChecked = permsInModule.every((p) => selected.has(p.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        permsInModule.forEach((p) => next.delete(p.id));
      } else {
        permsInModule.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const handleSave = () => {
    // Only submit IDs that are currently visible in the editor — filters out any
    // stale IDs from modules that were removed since hydration, or catalog entries
    // that never matched any rendered permission row.
    const visibleIds = new Set(
      Array.from(visiblePermissionsByModule.values()).flatMap((perms) => perms.map((p) => p.id)),
    );
    updateMutation.mutate(Array.from(selected).filter((id) => visibleIds.has(id)));
  };

  const isSaveDisabled =
    updateMutation.isPending ||
    isLoadingCatalog ||
    isLoadingEnabled ||
    isLoadingModules ||
    isBootstrapError;

  /**
   * True when all enabled modules have been loaded and the plan has zero
   * non-null-module groups to show.  Used for the empty-state hint.
   */
  const hasNoEnabledModules =
    !isLoadingModules && enabledModulesSet !== null && enabledModulesSet.size === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit Permissions — ${plan.name}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSave}
            disabled={isSaveDisabled}
            isLoading={updateMutation.isPending}
          >
            Save Permissions
          </Button>
        </>
      }
    >
      <ModalBody>
        {updateMutation.isError && (
          <FeedbackMessage $variant="error">
            {extractApiError(updateMutation.error, 'Failed to update permissions. Please try again.')}
          </FeedbackMessage>
        )}

        {successMessage && (
          <FeedbackMessage $variant="success">{successMessage}</FeedbackMessage>
        )}

        <FieldsetHeader>
          <Shield size={16} aria-hidden="true" />
          <FieldsetTitle>Fine-grained Permissions</FieldsetTitle>
        </FieldsetHeader>

        <HelperText>
          Select the permissions included in this plan's allow-list. Changes take effect
          immediately for all organizations on this plan — entitlement caches are evicted on save.
          Check a module header to toggle all permissions in that module at once.
        </HelperText>

        {isLoadingCatalog || isLoadingEnabled || isLoadingModules ? (
          <LoadingText>Loading permissions…</LoadingText>
        ) : isBootstrapError ? (
          <FeedbackMessage $variant="error">
            Failed to load plan data. Please close and reopen to retry.
          </FeedbackMessage>
        ) : (
          <GroupList>
            {hasNoEnabledModules && (
              <EmptyModulesHint>
                No modules are enabled for this plan. Enable modules first to see their permissions
                here. Only the Always Available group is shown below.
              </EmptyModulesHint>
            )}
            {Array.from(visiblePermissionsByModule.entries()).map(([moduleKey, perms]) => {
              // Single pass: derive selectedCount, allChecked, someChecked together
              const selectedCount = perms.reduce((n, p) => n + (selected.has(p.id) ? 1 : 0), 0);
              const allChecked = selectedCount === perms.length;
              const someChecked = !allChecked && selectedCount > 0;
              const moduleCheckboxId = `module-all-${plan.id}-${moduleKey}`;
              const isNullModule = moduleKey === NULL_MODULE_KEY;

              return (
                <ModuleGroup key={moduleKey}>
                  <ModuleHeader>
                    <HiddenCheckbox
                      id={moduleCheckboxId}
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={() => handleModuleToggle(moduleKey)}
                      disabled={updateMutation.isPending}
                      aria-label={`Select all in ${moduleLabel(moduleKey)}`}
                    />
                    <ModuleLabel htmlFor={moduleCheckboxId} $checked={allChecked} $partial={someChecked}>
                      <CheckboxBox $checked={allChecked} $partial={someChecked} aria-hidden="true">
                        {allChecked && <Checkmark />}
                        {someChecked && <PartialMark />}
                      </CheckboxBox>
                      <ModuleName>{moduleLabel(moduleKey)}</ModuleName>
                      <PermCount>
                        {selectedCount}/{perms.length}
                      </PermCount>
                    </ModuleLabel>
                    {isNullModule && (
                      <NullModuleNote>
                        These permissions are never license-gated; they pass through RBAC
                        regardless of plan. Include them here only for explicit auditability.
                      </NullModuleNote>
                    )}
                  </ModuleHeader>

                  <PermissionGrid role="group" aria-label={`Permissions in ${moduleLabel(moduleKey)}`}>
                    {perms.map((perm) => {
                      const id = `perm-${plan.id}-${perm.id}`;
                      const isChecked = selected.has(perm.id);
                      return (
                        <PermissionRow key={perm.id}>
                          <HiddenCheckbox
                            id={id}
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggle(perm.id)}
                            disabled={updateMutation.isPending}
                          />
                          <PermissionLabel htmlFor={id} $checked={isChecked}>
                            <CheckboxBox $checked={isChecked} $partial={false} aria-hidden="true">
                              {isChecked && <Checkmark />}
                            </CheckboxBox>
                            <PermissionText>
                              <PermissionName>{perm.displayName}</PermissionName>
                              <PermissionKey>{perm.key}</PermissionKey>
                            </PermissionText>
                          </PermissionLabel>
                        </PermissionRow>
                      );
                    })}
                  </PermissionGrid>
                </ModuleGroup>
              );
            })}
          </GroupList>
        )}
      </ModalBody>
    </Modal>
  );
};

export default EditPlanPermissionsModal;

// ==================== Styled Components ====================

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  max-height: 60vh;
  overflow-y: auto;
`;

const FeedbackMessage = styled.div<{ $variant: 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  ${({ $variant, theme }) =>
    $variant === 'error'
      ? `
    background: ${theme.colors.status.error.bg};
    border: 1px solid ${theme.colors.status.error.border};
    color: ${theme.colors.status.error.text};
  `
      : `
    background: ${theme.colors.status.success.bg};
    border: 1px solid ${theme.colors.status.success.border};
    color: ${theme.colors.status.success.text};
  `}
`;

const FieldsetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

const FieldsetTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ModuleGroup = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const ModuleHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

/** Visually hidden but accessible native checkbox */
const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const ModuleLabel = styled.label<{ $checked: boolean; $partial: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
  user-select: none;
`;

const ModuleName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`;

const PermCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const NullModuleNote = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  font-style: italic;
  line-height: 1.4;
`;

const EmptyModulesHint = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  line-height: 1.5;
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[3]};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PermissionRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const PermissionLabel = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
  user-select: none;

  &:hover > span:first-of-type {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const CheckboxBox = styled.span<{ $checked: boolean; $partial: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 2px solid
    ${({ $checked, $partial, theme }) =>
      $checked || $partial ? theme.colors.primary.base : theme.colors.borders.medium};
  background: ${({ $checked, $partial, theme }) =>
    $checked || $partial ? theme.colors.primary.base : theme.colors.backgrounds.white};
  transition: ${({ theme }) => theme.transitions.fast};
  margin-top: 1px;
`;

const PermissionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const PermissionName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.3;
`;

const PermissionKey = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  font-family: monospace;
`;

/** Simple SVG checkmark */
const Checkmark = () => (
  <svg
    width="10"
    height="8"
    viewBox="0 0 10 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1 3.5L3.5 6.5L9 1"
      stroke="#ffffff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Dash for indeterminate/partial state */
const PartialMark = () => (
  <svg
    width="10"
    height="2"
    viewBox="0 0 10 2"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M1 1H9" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
